import { GET_USER_PERMISSIONS } from '../query/permissionsQueries';
import {
  GetUserPermissions,
  GetUserPermissionsVariables,
} from '../query/__generated__/GetUserPermissions';
import { apolloClient } from '../index';
import { getUsernameFromToken } from './authUtils';

export interface UserAttributes {
  email?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  [key: string]: string | undefined;
}

/**
 * Gets user attributes from GraphQL API.
 * This keeps all data access consistent through the GraphQL API layer.
 * Uses GET_USER_PERMISSIONS to get all attributes including phone_number.
 *
 * @returns Promise<UserAttributes> User attributes from GraphQL
 * @throws Error if the query fails
 */
export async function getUserAttributes(): Promise<UserAttributes> {
  try {
    // Get username from token to use in GET_USER_PERMISSIONS query
    const username = await getUsernameFromToken();

    const { data } = await apolloClient.query<GetUserPermissions, GetUserPermissionsVariables>({
      query: GET_USER_PERMISSIONS,
      variables: { requestData: { username } },
      fetchPolicy: 'cache-first', // Use cache if available, but can fall back to network
    });

    if (!data?.userManagementGetUser?.user) {
      throw new Error('User data not available');
    }

    const user = data.userManagementGetUser.user;

    // All fields are guaranteed to be non-null per GraphQL schema (String!)
    const attributes: UserAttributes = {
      email: user.email,
      given_name: user.givenName,
      family_name: user.familyName,
      phone_number: user.phoneNumber,
    };

    return attributes;
  } catch (error) {
    console.error('Failed to get user attributes from GraphQL:', error);
    throw new Error('Failed to get user attributes from GraphQL');
  }
}
